import { MODULE_PROMPTS } from "../constants/modules";

export function parseOutputs(messages) {
  const outputs = {};
  if (!Array.isArray(messages)) return outputs;
  messages.forEach((m) => {
    if (m.role !== "assistant") return;
    const regex = /<<<OUTPUT_START:([^>]+)>>>([\s\S]*?)<<<OUTPUT_END>>>/g;
    let match;
    while ((match = regex.exec(m.content)) !== null) {
      outputs[match[1].trim()] = match[2].trim();
    }
  });
  return outputs;
}

export function detectCompletedPrompts(moduleId, messages) {
  const prompts = MODULE_PROMPTS[moduleId] || [];
  const completed = new Set();
  if (!Array.isArray(messages)) return completed;
  messages.forEach((m) => {
    if (m.role === "assistant") {
      prompts.forEach((p) => {
        if (m.content.includes(`✓ LOCKED: ${p}`)) completed.add(p);
      });
    }
  });
  return completed;
}

export function cleanMessageContent(content) {
  return content
    .replace(/<<<OUTPUT_START:[^>]+>>>[\s\S]*?<<<OUTPUT_END>>>/g, "")
    .replace(/✓ LOCKED: .+\n?/g, "")
    /* The whole turn, not just the marker line. These are app-injected user turns
       and every line of them is instruction addressed to Brill — but the rebuild
       variants run to a dozen lines, so stripping only the first left the rest
       rendering in the transcript as a wall of text telling Brill what to do.
       The plain "start this section" variant is a single line and hid correctly,
       which is why this survived: the multi-line ones arrived later. */
    .replace(/^\[PROMPT_START:[^\]]*\][\s\S]*/, "")
    .replace(/<<<TESTIMONIAL_SUBMITTED>>>[\s\S]*?<<<TESTIMONIAL_END>>>/g, "")
    /* A coach's Library proposal (utils/coachLibrary.js) is a card, never
       text — and an unclosed one is stripped to the end, not shown half-made. */
    .replace(/<<<LIBRARY_ITEM:[^>]*>>>[\s\S]*?(<<<LIBRARY_END>>>|$)/gi, "")
    /* Ask Brill's "open this tool" marker (utils/brillTools.js) is a button. */
    .replace(/<<<OPEN_TOOL:[^>]*>>>/gi, "")
    .trim();
}

export function extractLockedSections(content) {
  const names = [];
  const regex = /✓ LOCKED: (.+)/g;
  let match;
  while ((match = regex.exec(content)) !== null) {
    names.push(match[1].trim());
  }
  return names;
}

/* Every run of text ending in "?", one entry per question. Stops at sentence
   terminators and line breaks rather than using a lookbehind, which Safari only
   learned in 16.4 and a fair few coaches are on older phones. */
function questionsIn(text) {
  return text.match(/[^.!?\n]*\?/g) || [];
}

/* A question that hands her a decision about the thing being locked. The words
   are the ones Brill actually reaches for when it offers rather than asserts. */
const DECIDES_ABOUT_CONTENT =
  /\b(lock|adjust|change|tweak|revise|edit|reword|rework|amend|swap|sound right|look right|read right|work for you|happy with|prefer)\b/i;

/**
 * Is this reply still waiting on her before anything may be locked?
 *
 * Locking is the one irreversible-feeling thing Brill does — a section appears in
 * the sidebar with a tick and reads as settled — so it may never happen in the
 * same breath as asking her opinion. The module guides already say so
 * ("NEVER include OUTPUT_START, OUTPUT_END, or ✓ LOCKED markers in the same
 * response as an unanswered question"), but a prompt rule is a request, not an
 * enforcement, and this is the enforcement.
 *
 * Two different situations, deliberately not treated alike:
 *
 * - **Anything asked before the output block** is unanswered by definition. She
 *   cannot have replied to a question that arrives in the same message as its own
 *   answer. This half is the original inline guard, kept as it was.
 *
 * - **After the block**, questions are normal and mostly harmless — the guides
 *   tell Brill to offer the Blueprint the moment a module finishes, and blocking
 *   on that would stop the last section of every module from ever locking. What
 *   is not harmless is asking her to decide about the thing it is locking:
 *   "Want me to lock this, or adjust anything first?" is an offer, and treating an
 *   offer as consent is exactly what made this feel like it locked on her.
 *
 * Errs toward not locking. A false block costs her one more "yes"; a false lock
 * costs her a section she never agreed to, which is the complaint.
 */
export function hasUnansweredQuestion(reply) {
  if (!reply) return false;
  const outputStart = reply.indexOf("<<<OUTPUT_START:");
  if (outputStart === -1) return reply.includes("?");
  if (reply.slice(0, outputStart).includes("?")) return true;
  return questionsIn(reply.slice(outputStart)).some((q) => DECIDES_ABOUT_CONTENT.test(q));
}

/* ── Which module owns a section name ────────────────────────────────────────
 *
 * THE BUG THIS EXISTS FOR (2026-08-26). A module chat does not stop at the end
 * of its own curriculum. Brill finishes My Market, asks about the Blueprint, and
 * if the coach keeps talking it carries on being helpful — with the My Market
 * guide, into My Offer's territory. The output format in the preamble is
 * generic, so Brill happily emits `<<<OUTPUT_START:Your Signature Offer>>>`, and
 * `handleOutputLocked` files it under whichever module the chat happens to be.
 *
 * A live client did most of My Voice and a chunk of My Offer inside her My
 * Market chat. Eight sections — Voice Profile, Brand Feeling, Colour Palette,
 * Your Method, Your Signature Offer, Your Curriculum and two invented ones — all
 * stored as `module_key = 'market'`. Every one of them invisible: the sidebar
 * reads MODULE_PROMPTS.market, which has never heard of them, and Momentum reads
 * `module_key = 'offer'`, where they are not. From her side she locked her
 * Signature Offer, opened My Offer, and found nothing. It reads as "it only
 * locked in the chat."
 *
 * OWNERSHIP IS A SET, NOT A VALUE. `Pricing` lives in both `offer_ideation` and
 * `offer` — the only name in all 60 that does. A name is misfiled only when the
 * module it was locked in is not among its owners.
 *
 * A NAME NOBODY OWNS IS FINE AND MUST STAY FINE. Brill legitimately invents
 * variants — `Interview Debrief (Draft — Interview 2 of 3-5)`,
 * `Discovery Interview Questions — First Responder Guide` — and those are real
 * locked work sitting in production. A whitelist would throw them away. This
 * only ever refuses a name that demonstrably belongs somewhere else.
 */
const SECTION_OWNERS = (() => {
  const owners = {};
  Object.entries(MODULE_PROMPTS).forEach(([moduleId, names]) => {
    (names || []).forEach((name) => {
      if (!owners[name]) owners[name] = [];
      owners[name].push(moduleId);
    });
  });
  return owners;
})();

/** Every module that lists this section name. Empty for a Brill-invented one. */
export function sectionOwners(name) {
  return SECTION_OWNERS[name] ? [...SECTION_OWNERS[name]] : [];
}

/**
 * The module this section actually belongs to, when it isn't the one she's in.
 *
 * @returns {string|null} the owning module key, or null when locking here is fine
 *   — either because this module owns it, or because nobody does.
 */
export function belongsElsewhere(name, moduleId) {
  const owners = SECTION_OWNERS[name];
  if (!owners || !moduleId) return null;
  if (owners.includes(moduleId)) return null;
  return owners[0];
}

/**
 * Which sections a reply may actually lock, and which only claimed to.
 *
 * THE BUG THIS EXISTS FOR (2026-08-24). `✓ LOCKED: <name>` and the
 * `<<<OUTPUT_START>>>` block are two independent signals, and Brill can emit the
 * marker while writing the content as ordinary prose instead of wrapping it. The
 * caller used to resolve the name against `parseOutputs` over the WHOLE
 * conversation, so a lock with no fresh block reached backwards, found the
 * PREVIOUS version of that section, and locked that. A coach rebuilding after a
 * niche change watched Brill say it had rebuilt her Discovery Interview
 * Questions, then opened the locked card and found the old ones.
 *
 * Worse than the display: locking re-stamps the section with the CURRENT niche,
 * so re-locking old content quietly relabels it as up to date and `isStale`
 * stops flagging it. The bug laundered stale work into looking current, which is
 * the exact thing she was trying to audit.
 *
 * The discriminator is simply whether anything CHANGED. Content identical to
 * what is already saved means nothing new was written, whatever the marker says.
 * That keeps the legitimate flow working -- Brill writes the block, she says
 * "yes", and the marker arrives in the next message with no block repeated,
 * where the resolved content differs from what is stored and locks normally.
 *
 * THE SECOND BUG, THE MIRROR IMAGE (2026-08-27). The marker was the ONLY thing
 * that could start a lock. Brill wrote a complete `<<<OUTPUT_START:Mission
 * Expander>>>` block and simply omitted the `✓ LOCKED:` line, and because the
 * loop below iterates marker names, the loop body never ran: no card, no save,
 * no repair, no warning. Every guard stayed silent at once. Meanwhile the block
 * parsed fine everywhere else, so the transcript showed the work, the section
 * read as written, and nothing existed in `outputs`. From her side: "it said it
 * locked and it didn't."
 *
 * So A BLOCK IN THIS REPLY IS A CLAIM IN ITS OWN RIGHT. It is the stronger of
 * the two signals — the marker is a line of text asserting something, the block
 * IS the thing. A reply carrying one proposes a lock whether or not the marker
 * came with it, and she gets the card she would have got.
 *
 * The two claims are NOT treated identically in one respect. A marker whose
 * content matches what is already stored is reported as `unchanged`, because a
 * marker asserting a lock that changed nothing is the 2026-08-24 bug and the
 * caller repairs it. A block repeating what is already saved is not suspicious
 * and not worth an API call — it is simply already locked, so it is skipped.
 *
 * @param {string} reply          the assistant message just received
 * @param {object} resolved       { [section]: content } from parseOutputs
 * @param {object} savedOutputs   { [section]: content } already stored
 * @returns {{ sections: Array<{name, content}>, unchanged: string[], missing: string[] }}
 *   sections  - safe to lock
 *   unchanged - a MARKER claimed a lock, but the content matches what is stored
 *   missing   - claimed locked with no output block anywhere to resolve
 */
export function resolveLocks(reply, resolved = {}, savedOutputs = {}, moduleId = null) {
  const sections = [];
  const unchanged = [];
  const missing = [];

  /* Blocks written in THIS reply, as opposed to `resolved`, which spans the
     whole conversation. A name here has content by construction, so it can
     never land in `missing`. */
  const written = parseOutputs([{ role: "assistant", content: reply || "" }]);
  const marked = new Set(extractLockedSections(reply));
  const claimed = [...new Set([...marked, ...Object.keys(written)])];

  for (const name of claimed) {
    /* This reply's own block wins. `resolved` prefers the newest occurrence
       anyway, so this only matters when the caller passes an older map — and
       preferring what Brill just wrote is the honest reading either way. */
    const content = written[name] != null ? written[name] : resolved[name];
    if (content == null) { missing.push(name); continue; }
    const saved = savedOutputs[name];
    // Trimmed compare: the delimiters are stripped either side, and a stray
    // blank line is not a rebuild.
    if (saved != null && String(saved).trim() === String(content).trim()) {
      // Only a marker makes this suspicious — see the note above.
      if (marked.has(name)) unchanged.push(name);
      continue;
    }
    /* Where this actually goes.
     *
     * The key is only ADDED when there is something to say, rather than set to
     * null the rest of the time — three existing tests compare the whole section
     * object, and every caller that predates this guard should keep seeing the
     * exact shape it has always seen. A field that appears only when it means
     * something is also easier to read at a call site than one that is null on
     * 99% of locks.
     *
     * It REDIRECTS rather than refusing. Blocking would be the easy guard and
     * the wrong one: Brill has just written her a real Signature Offer, and
     * "that belongs elsewhere, go and do it again" burns the work to enforce a
     * filing rule. The content is hers either way — the only question is which
     * drawer it lands in, and we know the answer. */
    const belongsTo = belongsElsewhere(name, moduleId);
    sections.push(belongsTo ? { name, content, belongsTo } : { name, content });
  }
  return { sections, unchanged, missing };
}

/* ── What state is a section actually in? ─────────────────────────────────────
 *
 * THE RULE: storage decides, not the transcript.
 *
 * Three separate readers used to answer "is this locked?" by asking whether
 * Brill had typed `✓ LOCKED:` somewhere in the conversation -- the green card in
 * the transcript, the sidebar tick, and the content itself. None of them asked
 * whether a row exists in `outputs`. That is why:
 *   - a rebuild could not clear an old locked badge (nothing can un-say a line
 *     already in the transcript),
 *   - a section could read LOCKED while being absent from the database,
 *   - and a lock waiting on her word still rendered as done.
 *
 * `outputs` has exactly one writer, so a row IS the lock. The niche stamp on
 * that row is what separates work built under the niche she is on now from work
 * built under one she has left.
 *
 * UNSTAMPED MEANS CURRENT, deliberately, and this matches `isStale` in
 * ChatPanel. Sections locked before niche versioning shipped carry a null stamp
 * and are backfilled to the active version on load; treating null as stale
 * instead would flip every pre-August section in every account to "past" at
 * once. Nothing gets wrongly discounted.
 */
export const LOCK_STATE = {
  LOCKED: "locked",     // in storage, built under the niche she is on
  PAST: "past",         // in storage, built under a niche she has left
  UNSAVED: "unsaved",   // Brill said locked, but nothing was ever stored
};

export function sectionLockState({ name, savedOutputs, stamps, activeNicheId }) {
  const saved = savedOutputs ? savedOutputs[name] : undefined;
  // Empty content is not a lock. A row that somehow holds "" is a failed write,
  // and calling it locked is how she loses work without being told.
  if (saved == null || String(saved).trim() === "") return LOCK_STATE.UNSAVED;
  const stamp = stamps ? stamps[name] : undefined;
  if (!stamp || !activeNicheId) return LOCK_STATE.LOCKED;
  return stamp === activeNicheId ? LOCK_STATE.LOCKED : LOCK_STATE.PAST;
}

/* ── Why a lock did not save, in words she can act on ────────────────────────
 *
 * "It didn't save" is true and useless. Every one of these has a DIFFERENT next
 * action, and until now the strip said the same sentence for all of them and
 * put the real answer in a console she was never going to open.
 *
 * The one that prompted this: a browser tab left open across a deploy that
 * changed the database key keeps sending the OLD write, and Postgres answers
 * 42P10 — "no unique or exclusion constraint matching the ON CONFLICT
 * specification". Every lock in that tab fails, forever, and nothing on screen
 * says the word "refresh". The fix is five seconds and she had no way to know
 * it. A live client sat blocked for most of a day.
 *
 * Nothing here is a guess about the cause: each branch is keyed to a code
 * Postgres or PostgREST actually returned.
 */
export const LOCK_FAIL = {
  STALE_APP: "stale-app",     // 42P10 — this tab predates a schema change
  SIGNED_OUT: "signed-out",   // no Clerk user; nothing can be written at all
  DUPLICATE: "duplicate",     // 23505 — a row already holds this key
  OFFLINE: "offline",         // the request never reached PostgREST
  UNKNOWN: "unknown",
};

/** Classify a failed lock from what the database (or the network) said. */
export function lockFailureReason(error) {
  if (!error) return LOCK_FAIL.UNKNOWN;
  const code = error.code || "";
  const message = String(error.message || "");
  if (code === "42P10" || /ON CONFLICT specification/i.test(message)) return LOCK_FAIL.STALE_APP;
  if (code === "23505") return LOCK_FAIL.DUPLICATE;
  /* PostgREST never answered. `TypeError: Failed to fetch` is what a dropped
     connection looks like from the browser, and it is worth separating because
     "try again" genuinely is the right advice for it and not for the others. */
  if (code === "" && /failed to fetch|networkerror|load failed/i.test(message)) return LOCK_FAIL.OFFLINE;
  return LOCK_FAIL.UNKNOWN;
}

/** What to tell her, and whether pressing "Try again" can possibly help. */
export function lockFailureAdvice(reason) {
  switch (reason) {
    case LOCK_FAIL.STALE_APP:
      return { text: "Brill was updated while this page was open. Refresh, then lock it again.", canRetry: false };
    case LOCK_FAIL.SIGNED_OUT:
      return { text: "You’ve been signed out, so nothing can be saved. Sign back in and lock it again.", canRetry: false };
    case LOCK_FAIL.DUPLICATE:
      return { text: "Something is already stored under this section. Refresh to see what’s there before locking over it.", canRetry: false };
    case LOCK_FAIL.OFFLINE:
      return { text: "Brill couldn’t reach the server — that’s usually the connection.", canRetry: true };
    default:
      return { text: "", canRetry: true };
  }
}

/** The sections of a module that are genuinely done: stored, and current. */
export function lockedSections({ prompts = [], savedOutputs, stamps, activeNicheId }) {
  return new Set(
    prompts.filter((name) =>
      sectionLockState({ name, savedOutputs, stamps, activeNicheId }) === LOCK_STATE.LOCKED)
  );
}

/* ── Did she just confirm the lock, or only mention it? ──────────────────────
 *
 * A card is on screen offering "Lock it". She can press it, and she can also say
 * so — and BOTH have been wrong in opposite directions.
 *
 * TOO LOOSE. The old rule was a word-boundary match on lock-or-save within eight
 * words. So "no don't lock it" LOCKED IT — the negation list held `dont` and
 * `do not` but not `don't` — and so did "how do I save this?", "does this lock
 * automatically?" and "I lock in my goals every Monday". Nine of fourteen sample
 * phrases were wrong. Since Stage 3 a false positive writes Brill's proposal
 * into her section for the current niche.
 *
 * TOO TIGHT. Brill's own guides say "Say yes to lock it in" in six places, and
 * bare "yes" matched nothing at all. The product asked for a word it did not
 * listen for.
 *
 * THE LAST SENTENCE, not the whole message. A live client confirmed with "This
 * mapping feels right. Can we lock it?" — any whole-message rule rejects that,
 * and a "questions never lock" rule would reject it too while still letting "how
 * do I save this?" through. The difference is a REQUEST versus an ENQUIRY, and
 * the last sentence is where the request lives.
 *
 * THREE OUTCOMES, and the third is what makes tightening safe here. Every other
 * fix in this seam added a way for a lock to quietly not happen. A near miss is
 * never silent: the card says "Did you mean to lock this?" and points at the
 * button. So the worst case of being too strict is one extra click, never lost
 * work — which is also the direction we want people moving anyway.
 *
 * NEGATION AND QUALIFIERS GET NOTHING, not a prompt. "no don't lock it" and
 * "lock it but change the second line" have already said what they meant;
 * asking "did you mean to lock this?" would be arguing with her.
 */
export const LOCK_REPLY = {
  CONFIRM: "confirm",     // do what the button does
  NEAR_MISS: "near-miss", // do not lock, but say so on the card
  NONE: "none",           // an ordinary message
};

const LOCK_MENTIONED = /\b(lock|save)\b/i;
/* Refusals AND deferrals. "later" and "not yet" sit here rather than with
   the near misses because "can you save that for later" is her saying not
   now — she has already answered, and a "did you mean to lock this?" prompt
   would argue with her. The test table had it filed as a near miss and the
   run corrected it. */
/* Refusals AND deferrals. "later" sits here rather than with the near misses
   because "can you save that for later" is her saying not now — she has already
   answered, and "did you mean to lock this?" would argue with her. The test
   table had it filed as a near miss and the run corrected it.

   THE APOSTROPHE IS THE WHOLE POINT. The rule this replaces listed `dont` and
   `do not` but not `don't`, so "no don't lock it" LOCKED IT. Both spellings are
   covered here and both are pinned by a named test. */
const LOCK_REFUSED = /\b(no|not|never|don'?t|dont|stop|wait|cancel|hold off|later|not yet)\b/i;
const LOCK_QUALIFIED = /\b(but|except|although|however|instead|change|tweak|adjust|reword|first)\b/i;

/* Bare consent — "yes", "ok", "sure". This POINTS AT THE BUTTON rather than
   locking, and that is her call, 2026-09-01: "no, yes should pull up the button
   and then she can lock it."

   The reasoning is a risk I introduced earlier the same day. A bare "yes"
   answers whatever Brill last asked, and while a card is on screen Brill can ask
   about something else entirely — "want me to cover pricing too?" — so treating
   it as consent to STORE would lock on a yes she meant for the question. Naming
   the action cannot be mistaken that way, so "lock it" still locks outright.

   "great" and "perfect" are not here at all: reactions to the work, not answers.
   They fall through to NONE. */
const AFFIRMATION = /^(yes|yeah|yep|yup|ok|okay|sure|agreed|confirmed|correct|do it|go ahead)\b/i;

/* A request to lock: an optional yes, optional politeness or asking frame, then
   the verb. Anchored, so "I lock in my goals every Monday" cannot match — the
   verb has to be what the sentence is FOR, not a word inside it. */
const LOCK_REQUEST =
  /^((yes|yeah|yep|ok|okay|sure)[,!.\s]+)?((please|can we|can you|could we|could you|let'?s|lets|i'?d like to|ready to|go ahead and)\s+)*(lock|save)\b/i;

/** The final sentence, which is where a confirmation lives when one is offered. */
function lastSentence(text) {
  const parts = String(text).split(/[.!?]+/).map((s) => s.trim()).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : String(text).trim();
}

/**
 * How to read what she typed while a lock card is on screen.
 * Callers must only apply this WHILE a card is up — it says nothing about
 * whether there is anything to lock.
 */
export function readLockReply(text) {
  const raw = String(text || "").trim();
  if (!raw) return LOCK_REPLY.NONE;
  if (LOCK_REFUSED.test(raw) || LOCK_QUALIFIED.test(raw)) return LOCK_REPLY.NONE;

  const last = lastSentence(raw);
  const mentioned = LOCK_MENTIONED.test(raw);
  /* The eight-word ceiling is kept from the original rule and applied to the
     sentence rather than the message, so a short confirmation after a long
     thought still counts. */
  if (last.split(/\s+/).length > 8) return mentioned ? LOCK_REPLY.NEAR_MISS : LOCK_REPLY.NONE;
  /* NAMING THE ACTION LOCKS. "lock it", "save it", "can we lock it", and
     "yes, lock option 3" — a live client's own words — cannot be a yes to
     something else, so they do what the button does. */
  if (LOCK_REQUEST.test(last)) return LOCK_REPLY.CONFIRM;
  /* A BARE YES POINTS AT THE BUTTON. The card is already on screen; the line it
     grows says "Press Lock it", which is where she is meant to be going. */
  if (AFFIRMATION.test(last)) return LOCK_REPLY.NEAR_MISS;
  return mentioned ? LOCK_REPLY.NEAR_MISS : LOCK_REPLY.NONE;
}

/* ── Built, and whether it still fits the niche she is on ────────────────────
 *
 * TWO DIFFERENT QUESTIONS, and the sidebar was answering only the first:
 *
 *   "Has she done this work?"        → built  (stored under ANY niche)
 *   "Does it still fit her niche?"   → past   (stored under one she has left)
 *
 * Using LOCKED-only for both is what emptied her sidebar. After a niche change
 * every section she had completed drew a hollow circle — pixel-identical to one
 * she had never started — the module read 0%, and clicking a row started the
 * prompt over instead of opening work that was sitting in the database. Her
 * words, 2026-09-01: "she did complete them so they should be checked and count
 * toward her total", with the phase counter turning salmon until she either
 * confirms them or redoes them.
 *
 * ONE PASS, ONE HELPER, BOTH SETS. The tick, the count, the click target and the
 * salmon all read from this, so they cannot drift apart the next time one of
 * them is edited — the same reason Momentum has a single `pageState`.
 *
 * `lockedSections` above is unchanged and still means CURRENT-only. ChatPanel's
 * transition card keeps using it deliberately: "have you built this?" and "is
 * this module ready to build on?" are not the same question, and a module built
 * entirely on an abandoned niche should still be offered for review.
 */
export function sectionProgress({ prompts = [], savedOutputs, stamps, activeNicheId }) {
  const built = new Set();
  const past = new Set();
  prompts.forEach((name) => {
    const state = sectionLockState({ name, savedOutputs, stamps, activeNicheId });
    if (state === LOCK_STATE.LOCKED || state === LOCK_STATE.PAST) built.add(name);
    if (state === LOCK_STATE.PAST) past.add(name);
  });
  return { built, past };
}

/* ── Putting an optimistic lock back ─────────────────────────────────────────
 *
 * `handleOutputLocked` writes the section into local state before the database
 * confirms it, so the tick appears instantly. If the write then fails, that tick
 * is a lie and has to come off — otherwise the sidebar says locked while the
 * amber strip says it didn't save, which is two readers disagreeing about one
 * section, the exact thing the lock work exists to end.
 *
 * `undefined` means the section had no value before this attempt — she was
 * locking it for the first time. The key is DELETED rather than set to
 * undefined, because `{ "Pricing": undefined }` still answers true to
 * `"Pricing" in map` and to `Object.keys`, and `sectionLockState` would read it
 * as a row that exists holding nothing.
 */
export function restoreSectionValue(map, moduleKey, sectionName, value) {
  const next = { ...((map || {})[moduleKey] || {}) };
  if (value === undefined) delete next[sectionName];
  else next[sectionName] = value;
  return { ...(map || {}), [moduleKey]: next };
}

/* ── Where the niche changed, in the middle of a conversation ────────────────
 *
 * THE PROBLEM. A module's whole transcript goes to the API, and for a coach who
 * pivoted mid-module that transcript holds the reasoning behind BOTH niches —
 * same section names, contradictory foundations, no marker between them. Brill
 * has already been caught reading old locked work out of a transcript and
 * following its guide's "then move to X" chain from there.
 *
 * WORSE, IT IS NON-DETERMINISTIC. `api/chat.js` trims to the last 40 messages.
 * So whether Brill sees the old niche's reasoning depends on how much she has
 * said since — a short module hands it over in full, a long one has already
 * dropped it. Same product, same pivot, opposite behaviour, and nothing tells
 * her which. That is the guessing this is here to remove.
 *
 * THE FIX IS A BOUNDARY, NOT A FILTER. Dropping the old messages would make
 * Brill amnesiac about a conversation she remembers having. The transcript is
 * chronological and the pivot has a timestamp, so it only needs a line drawn in
 * the right place. She can still ask about anything above it.
 *
 * ROBUST UNDER TRIMMING BY CONSTRUCTION: the marker is newer than every message
 * it follows, so if any pre-pivot message survives the 40-message window, the
 * marker survives with it. There is no state where Brill sees the old reasoning
 * without the warning.
 *
 * A USER TURN, NOT AN ASSISTANT ONE. An assistant message reads to the model as
 * something it already said, and it defers to its own guide instead — that is
 * why the Redo flow once answered "let me start fresh". Instructions arrive in a
 * user turn or they don't arrive.
 *
 * Messages with no `created_at` are never counted as "before": the module opener
 * is prepended without one, and anything typed this session is happening now,
 * which is after any pivot by definition.
 */
export function withNicheBoundary(messages, activeNiche) {
  if (!Array.isArray(messages) || messages.length === 0) return messages;
  const since = activeNiche?.activated_at;
  if (!since) return messages;

  let lastBefore = -1;
  for (let i = 0; i < messages.length; i++) {
    const at = messages[i]?.created_at;
    if (at && at < since) lastBefore = i;
  }
  // Nothing predates the pivot, so there is no line to draw.
  if (lastBefore === -1) return messages;

  const label = activeNiche.label || "a new niche";
  const statement = activeNiche.statement ? ` — ${activeNiche.statement}` : "";
  const marker = {
    role: "user",
    content:
      "[NICHE_CHANGED — read only, do not reference as a conversation]\n"
      + "Everything above this line was written for a niche she has since left. "
      + `She is now building for: ${label}${statement}.\n`
      + "Do not treat anything above as settled for the niche she is on now, and "
      + "do not carry its conclusions forward on your own. You may refer to it if "
      + "she asks about it directly.",
  };

  return [...messages.slice(0, lastBefore + 1), marker, ...messages.slice(lastBefore + 1)];
}

/* Niche Clarity is RETIRED from My Client (2026-08-27).
 *
 * The niche moved to My Mission a long time ago. This used to keep the section
 * visible for anyone who had already locked it — "grandfathered in" — by asking
 * `detectCompletedPrompts` whether the TRANSCRIPT said it was done.
 *
 * Two reasons it goes:
 *
 * 1. It was the last place in the app that decided anything user-facing from
 *    the conversation rather than from storage. Everything else reads
 *    `outputs`. A transcript line cannot be un-said, so this section could
 *    never be got rid of by any action the coach took.
 *
 * 2. It made the section un-removable in the worst way. Deleting the stored row
 *    did not hide it — the transcript still said it was locked, so the section
 *    stayed in the sidebar with nothing behind it. A permanently grey hole she
 *    could not fill, because the prompt that fills it lives in My Mission now.
 *
 * NOTHING IS DELETED. Anyone's locked Niche Clarity content stays exactly where
 * it is in `outputs`; it simply stops being offered as a section of My Client.
 * The `msgs` argument is kept so the five call sites need no edit, and because
 * a future rule may want it again.
 */
export function getVisiblePrompts(moduleId, msgs) {   // eslint-disable-line no-unused-vars
  const prompts = MODULE_PROMPTS[moduleId] || [];
  if (moduleId !== "client") return prompts;
  return prompts.filter((p) => p !== "Niche Clarity");
}

/**
 * "2:38 PM" for one of her messages, or null.
 *
 * Only her turns are stamped — the mockup marks when SHE spoke, and a stamp on
 * every bubble turns a conversation into a log. Null rather than "now" when
 * `created_at` is missing, because a made-up time is worse than no time:
 * history predating this change has no stamp to show.
 */
export function sentAt(m) {
  if (m?.role !== "user" || !m?.created_at) return null;
  const d = new Date(m.created_at);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}


/**
 * "Today", "Sep 17", or "Sep 17, 2025" — the day a message was sent, as short
 * as it can be while still being unambiguous.
 *
 * Today is named rather than dated because a date on a conversation she is
 * having right now is noise. The year appears only when it isn't this one: a
 * module she worked through last September reading "Sep 17" would be a lie of
 * omission.
 */
function dayLabel(d, now) {
  if (d.toDateString() === now.toDateString()) return "Today";
  return d.toLocaleDateString("en-US", d.getFullYear() === now.getFullYear()
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" });
}

/**
 * The stamp under each bubble, one entry per message and parallel to the
 * transcript it was given. Null where nothing should render.
 *
 * The day is only printed where it CHANGES — her call, 2026-09-18: the same
 * date repeated down a whole module is noise, but a turn from before today has
 * to say when it was. Computed across the transcript rather than per message
 * because "has the day changed" is a question about the message BEFORE this
 * one, and the answer has to survive the assistant replies in between.
 *
 * Whole-transcript, not per-render-per-message: this feeds a loop that a long
 * module runs hundreds of times.
 */
export function messageStamps(messages, now = new Date()) {
  let lastDay = null;
  return (Array.isArray(messages) ? messages : []).map((m) => {
    const time = sentAt(m);
    if (!time) return null;          // not hers, or no time to show
    const day = new Date(m.created_at).toDateString();
    const changed = day !== lastDay;
    lastDay = day;
    return changed ? `${dayLabel(new Date(m.created_at), now)} · ${time}` : time;
  });
}
